import { useEffect, useRef, useCallback } from 'react';
import './MeteorMashAnimation.css';

/**
 * PROFESSIONAL METEOR MASH ANIMATION
 * A powerful steel-type punching move with cosmic meteor effects
 * 
 * Key Features:
 * - Multi-phase animation with cosmic energy buildup
 * - Meteor-like projectiles that converge on impact
 * - Steel-type themed with metallic particles and star effects
 * - Impact crater effect with shockwaves
 * - Professional game-quality visual effects matching Pokémon style
 */
export default function MeteorMashAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const meteorsRef = useRef([]);
  
  // Animation phase transition hook - using console logs for debugging
  const transitionTo = useCallback((phase, delay = 0) => {
    setTimeout(() => console.log(`Animation phase: ${phase}`), delay);
  }, []);
  
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container
    const container = playerEl.closest('.battle-container');
    if (!container) {
      console.error('Battle container not found');
      onComplete();
      return;
    }

    // Get positions
    const p = playerEl.getBoundingClientRect();
    const o = opponentEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate positions relative to container
    const playerX = p.left - containerRect.left + p.width / 2;
    const playerY = p.top - containerRect.top + p.height / 2;
    const opponentX = o.left - containerRect.left + o.width / 2;
    const opponentY = o.top - containerRect.top + o.height / 2;

    // TIMING CONFIGURATION - Professional gaming timing
    const TIMING = {
      preparation: 300,     // Player stance and initial energy
      energyBuild: 400,     // Cosmic energy buildup
      meteorSummon: 300,    // Summon meteors
      meteorTravel: 400,    // Meteors travel to target
      impact: 350,          // Impact and explosion
      craterEffect: 250,    // Crater effect
      returnToPosition: 200, // Return to original position
      total: 2200,          // Total animation duration
    };

    console.log('☄️ Meteor Mash Animation Starting');
    
    // PHASE 1: PREPARATION (0-300ms)
    // Player prepares with a fighting stance
    transitionTo('preparation');
    playerEl.style.transition = 'transform 200ms ease-in-out';
    playerEl.style.transform = 'scale(1.1) rotate(-3deg)';
    
    // Create cosmic energy buildup effect around player's arm/fist
    const energyBuildupEffect = document.createElement('div');
    energyBuildupEffect.className = 'meteor-mash-energy-buildup';
    energyBuildupEffect.style.left = `${playerX}px`;
    energyBuildupEffect.style.top = `${playerY}px`;
    container.appendChild(energyBuildupEffect);
    
    // PHASE 2: ENERGY BUILD (300-700ms)
    const energyBuildTimer = setTimeout(() => {
      transitionTo('energy_build');
      console.log('☄️ Phase 2: Cosmic Energy Build');
      
      // Enhance energy buildup with stars
      for (let i = 0; i < 8; i++) {
        setTimeout(() => {
          createStar(container, playerX, playerY, 40);
        }, i * 40);
      }
      
      // Player arm glow effect intensifies
      playerEl.classList.add('meteor-mash-charging');
      
    }, TIMING.preparation);
    
    // PHASE 3: METEOR SUMMON (700-1000ms)
    const meteorSummonTimer = setTimeout(() => {
      transitionTo('meteor_summon');
      console.log('☄️ Phase 3: Meteor Summon');
      
      // Create cosmic meteors around player
      const meteorCount = 5;
      const radius = 70; // Distance from player center
      
      for (let i = 0; i < meteorCount; i++) {
        const angle = (i / meteorCount) * Math.PI * 2;
        const angleDeg = Math.round((angle * 180) / Math.PI);
        
        setTimeout(() => {
          const meteor = document.createElement('div');
          meteor.className = 'meteor-mash-meteor';
          
          // Position meteor around player
          const meteorX = playerX + Math.cos(angle) * radius;
          const meteorY = playerY + Math.sin(angle) * radius;
          
          meteor.style.left = `${meteorX}px`;
          meteor.style.top = `${meteorY}px`;
          
          // Set CSS variables for animation
          meteor.style.setProperty('--angle', `${angleDeg}deg`);
          meteor.style.setProperty('--delay', `${i * 0.1}s`);
          
          container.appendChild(meteor);
          meteorsRef.current.push(meteor);
          
          // Create meteor trail
          createMeteorTrail(container, meteorX, meteorY);
          
          console.log(`☄️ Meteor ${i + 1} created at angle ${angleDeg}°`);
        }, i * 50); // Staggered appearance
      }
      
    }, TIMING.preparation + TIMING.energyBuild);
    
    // PHASE 4: METEOR TRAVEL (1000-1400ms)
    const meteorTravelTimer = setTimeout(() => {
      transitionTo('meteor_travel');
      console.log('☄️ Phase 4: Meteor Travel');
      
      // Move all meteors toward opponent
      meteorsRef.current.forEach((meteor, index) => {
        meteor.classList.add('meteor-mash-traveling');
        
        // Animate travel to opponent with slight offset for each meteor
        const offsetX = -20 + Math.random() * 40;
        const offsetY = -20 + Math.random() * 40;
        
        meteor.style.transition = 'left 0.4s cubic-bezier(0.2, 0.8, 0.8, 0.8), top 0.4s cubic-bezier(0.2, 0.8, 0.8, 0.8)';
        meteor.style.left = `${opponentX + offsetX}px`;
        meteor.style.top = `${opponentY + offsetY}px`;
        
        // Create travel trail particles
        const startX = parseFloat(meteor.style.left);
        const startY = parseFloat(meteor.style.top);
        
        for (let i = 0; i < 3; i++) {
          setTimeout(() => {
            const progress = (i + 1) / 4;
            const trailX = startX + (opponentX + offsetX - startX) * progress;
            const trailY = startY + (opponentY + offsetY - startY) * progress;
            createMeteorTrail(container, trailX, trailY);
          }, i * 100);
        }
      });
      
      // Player starts punch motion
      playerEl.style.transition = 'transform 0.3s ease-in-out';
      playerEl.style.transform = 'scale(1.05) translateX(20px) rotate(-2deg)';
      
    }, TIMING.preparation + TIMING.energyBuild + TIMING.meteorSummon);
    
    // PHASE 5: IMPACT (1400-1750ms)
    const impactTimer = setTimeout(() => {
      transitionTo('impact');
      console.log('☄️ Phase 5: Impact');
      
      // Remove energy buildup
      if (energyBuildupEffect.parentNode) {
        energyBuildupEffect.parentNode.removeChild(energyBuildupEffect);
      }
      
      // Create impact explosion
      const impact = document.createElement('div');
      impact.className = 'meteor-mash-impact';
      impact.style.left = `${opponentX}px`;
      impact.style.top = `${opponentY}px`;
      container.appendChild(impact);
      animRef.current = impact;
      
      // Create impact flash
      const flash = document.createElement('div');
      flash.className = 'meteor-mash-flash';
      flash.style.left = `${opponentX}px`;
      flash.style.top = `${opponentY}px`;
      container.appendChild(flash);
      
      // Create metallic particles
      createMetallicParticles(container, opponentX, opponentY, 15);
      
      // Create star particles
      for (let i = 0; i < 6; i++) {
        setTimeout(() => {
          createStar(container, opponentX, opponentY, 60);
        }, i * 50);
      }
      
      // Opponent reaction
      opponentEl.classList.add('meteor-mash-hit');
      
      // Remove meteors with small explosions
      meteorsRef.current.forEach((meteor, index) => {
        setTimeout(() => {
          const meteorX = parseFloat(meteor.style.left);
          const meteorY = parseFloat(meteor.style.top);
          
          // Small explosion at meteor position
          const smallExplosion = document.createElement('div');
          smallExplosion.className = 'meteor-mash-small-explosion';
          smallExplosion.style.left = `${meteorX}px`;
          smallExplosion.style.top = `${meteorY}px`;
          container.appendChild(smallExplosion);
          
          // Remove meteor
          meteor.remove();
          
          // Remove small explosion after animation
          setTimeout(() => {
            smallExplosion.remove();
          }, 300);
        }, index * 50);
      });
      
      // Clean up flash
      setTimeout(() => {
        if (flash.parentNode) {
          flash.parentNode.removeChild(flash);
        }
      }, 200);
      
    }, TIMING.preparation + TIMING.energyBuild + TIMING.meteorSummon + TIMING.meteorTravel);
    
    // PHASE 6: CRATER EFFECT (1750-2000ms)
    const craterTimer = setTimeout(() => {
      transitionTo('crater_effect');
      console.log('☄️ Phase 6: Crater Effect');
      
      // Create crater shockwave
      const crater = document.createElement('div');
      crater.className = 'meteor-mash-crater';
      crater.style.left = `${opponentX}px`;
      crater.style.top = `${opponentY}px`;
      container.appendChild(crater);
      
      // Create secondary particles
      createMetallicParticles(container, opponentX, opponentY, 8);
      
      // Enhanced opponent reaction
      opponentEl.classList.add('meteor-mash-hit-strong');
      
      // Clean up crater after animation
      setTimeout(() => {
        if (crater.parentNode) {
          crater.parentNode.removeChild(crater);
        }
        opponentEl.classList.remove('meteor-mash-hit');
        opponentEl.classList.remove('meteor-mash-hit-strong');
      }, 250);
      
    }, TIMING.preparation + TIMING.energyBuild + TIMING.meteorSummon + TIMING.meteorTravel + TIMING.impact);
    
    // PHASE 7: RETURN TO POSITION (2000-2200ms)
    const returnTimer = setTimeout(() => {
      transitionTo('return');
      console.log('☄️ Phase 7: Return to Position');
      
      // Remove impact
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
      }
      
      // Return player to original position
      playerEl.style.transition = 'transform 200ms ease-out';
      playerEl.style.transform = '';
      playerEl.classList.remove('meteor-mash-charging');
      
      // Animation complete
      setTimeout(() => {
        console.log('☄️ Meteor Mash Animation Complete');
        onComplete();
      }, 200);
      
    }, TIMING.preparation + TIMING.energyBuild + TIMING.meteorSummon + TIMING.meteorTravel + TIMING.impact + TIMING.craterEffect);
    
    // Helper function to create metallic particles
    function createMetallicParticles(container, x, y, count) {
      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div');
        particle.className = 'meteor-mash-particle';
        
        // Random position around impact point
        const angle = Math.random() * Math.PI * 2;
        const distance = 5 + Math.random() * 30;
        particle.style.left = `${x + Math.cos(angle) * distance}px`;
        particle.style.top = `${y + Math.sin(angle) * distance}px`;
        
        // Random properties for varied effect
        particle.style.setProperty('--rotation', `${Math.random() * 360}deg`);
        particle.style.setProperty('--size', `${3 + Math.random() * 5}px`);
        particle.style.setProperty('--duration', `${0.3 + Math.random() * 0.4}s`);
        
        container.appendChild(particle);
        particlesRef.current.push(particle);
        
        // Auto-remove particles
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
            particlesRef.current = particlesRef.current.filter(p => p !== particle);
          }
        }, 400 + Math.random() * 300);
      }
    }
    
    // Helper function to create star particles
    function createStar(container, x, y, maxDistance) {
      const star = document.createElement('div');
      star.className = 'meteor-mash-star';
      
      // Random position around center point
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.random() * maxDistance;
      star.style.left = `${x + Math.cos(angle) * distance}px`;
      star.style.top = `${y + Math.sin(angle) * distance}px`;
      
      // Random properties for varied effect
      star.style.setProperty('--rotation', `${Math.random() * 360}deg`);
      star.style.setProperty('--size', `${4 + Math.random() * 6}px`);
      star.style.setProperty('--duration', `${0.4 + Math.random() * 0.5}s`);
      
      container.appendChild(star);
      particlesRef.current.push(star);
      
      // Auto-remove stars
      setTimeout(() => {
        if (star.parentNode) {
          star.parentNode.removeChild(star);
          particlesRef.current = particlesRef.current.filter(p => p !== star);
        }
      }, 500 + Math.random() * 300);
    }
    
    // Helper function to create meteor trail
    function createMeteorTrail(container, x, y) {
      const trail = document.createElement('div');
      trail.className = 'meteor-mash-trail';
      trail.style.left = `${x}px`;
      trail.style.top = `${y}px`;
      
      // Random size for varied effect
      trail.style.setProperty('--size', `${8 + Math.random() * 8}px`);
      
      container.appendChild(trail);
      particlesRef.current.push(trail);
      
      // Auto-remove trail
      setTimeout(() => {
        if (trail.parentNode) {
          trail.parentNode.removeChild(trail);
          particlesRef.current = particlesRef.current.filter(p => p !== trail);
        }
      }, 300 + Math.random() * 200);
    }
    
    // Helper function to clean up particles
    function cleanupParticles() {
      particlesRef.current.forEach(particle => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      });
      particlesRef.current = [];
      
      meteorsRef.current.forEach(meteor => {
        if (meteor.parentNode) {
          meteor.parentNode.removeChild(meteor);
        }
      });
      meteorsRef.current = [];
    }

    // Cleanup function
    return () => {
      // Clear all timers
      clearTimeout(energyBuildTimer);
      clearTimeout(meteorSummonTimer);
      clearTimeout(meteorTravelTimer);
      clearTimeout(impactTimer);
      clearTimeout(craterTimer);
      clearTimeout(returnTimer);
      
      // Clean up energy buildup
      if (energyBuildupEffect.parentNode) {
        energyBuildupEffect.parentNode.removeChild(energyBuildupEffect);
      }
      
      // Clean up main animation element
      if (animRef.current && animRef.current.parentNode) {
        animRef.current.parentNode.removeChild(animRef.current);
        animRef.current = null;
      }
      
      // Clean up particles
      cleanupParticles();
      
      // Reset states
      if (playerEl) {
        playerEl.style.transition = '';
        playerEl.style.transform = '';
        playerEl.classList.remove('meteor-mash-charging');
      }
      if (opponentEl) {
        opponentEl.classList.remove('meteor-mash-hit');
        opponentEl.classList.remove('meteor-mash-hit-strong');
      }
      
      console.log('☄️ Meteor Mash Animation: Cleanup completed');
    };
  }, [playerRef, opponentRef, onComplete, transitionTo]);

  return null;
}

// Set the move type for proper coloring in the battle UI
MeteorMashAnimation.moveType = "steel";
